import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { staffApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Section, Field, Tabs } from "../../Components/UI/ui";
import { GATE, fullName, cap } from "../../lib/lab";
import { ProfileFields, profileToForm, profileToBody, SignatureUpload } from "./MemberForms";
import Members from "./Members";
import Letterhead from "./Letterhead";
import LabelStock from "./LabelStock";

// Settings hub: My profile · Members · Letterhead · Labs · Security
function Setting() {
  const { user, labId, labRole, labs, can, switchLab, changePassword, updateStaff } = useAuth();
  const { toast } = useApp();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const setTab = (t) => setParams({ tab: t });

  const tabs = [
    { id: "profile", label: "My profile" },
    { id: "members", label: "Members" },
    ...(can(...GATE.branding) ? [{ id: "letterhead", label: "Letterhead" }] : []),
    { id: "labels", label: "Label stock" },
    ...(labs?.length > 1 ? [{ id: "labs", label: "Labs" }] : []),
    { id: "security", label: "Security" },
  ];

  return (
    <Page title="Settings" subtitle={`${fullName(user?.staff?.name)} · ${cap(labRole)}`}>
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      <div className="lab-page__body">
        {tab === "profile" && <MyProfile user={user} labRole={labRole} updateStaff={updateStaff} toast={toast} />}
        {tab === "members" && <Members />}
        {tab === "letterhead" && <Letterhead />}
        {tab === "labels" && <LabelStock />}
        {tab === "labs" && <LabSwitcher labs={labs} labId={labId} switchLab={switchLab} toast={toast} />}
        {tab === "security" && <Security changePassword={changePassword} toast={toast} />}
      </div>
    </Page>
  );
}

// PUT /labStaff/:staffId on yourself (qualification, reg. no., phone…) + signature
const MyProfile = ({ user, labRole, updateStaff, toast }) => {
  const staff = user?.staff || {};
  const staffId = staff.staffId || user?.staffId || staff._id;
  const [f, setF] = useState(profileToForm(staff));
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  // /auth/me's staff object is slim; fetch the member record for qualification etc.
  useEffect(() => {
    if (!staffId) return;
    staffApi.get(staffId).then((m) => { setMe(m); setF(profileToForm(m)); }).catch(() => {});
  }, [staffId]);

  const save = async () => {
    setBusy(true);
    try {
      const saved = await staffApi.update(staffId, profileToBody(f));
      setMe(saved?._id ? saved : { ...me, ...profileToBody(f) });
      updateStaff(saved?._id ? saved : profileToBody(f));
      setEditing(false);
      toast.success("Profile saved");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const m = me || staff;
  const isSignatory = labRole === "signatory" || labRole === "admin" || labRole === "owner";

  return (
    <div className="lab-grid cols-2" style={{ alignItems: "start" }}>
      <Section title="Profile" right={!editing && <Button size="sm" onClick={() => setEditing(true)}>Edit</Button>}>
        {editing ? (
          <>
            <ProfileFields f={f} set={set} />
            <div className="lab-row end" style={{ marginTop: 12 }}>
              <Button onClick={() => { setEditing(false); setF(profileToForm(m)); }}>Cancel</Button>
              <Button variant="primary" loading={busy} onClick={save}>Save</Button>
            </div>
          </>
        ) : (
          <dl className="lab-kv">
            <dt>Name</dt><dd>{fullName(m.name)}</dd>
            <dt>Job title</dt><dd>{cap(m.staffType) || "—"}</dd>
            <dt>Qualification</dt><dd>{m.qualification || <span className="lab-muted">— {isSignatory ? "(prints under your signature)" : ""}</span>}</dd>
            <dt>Registration no.</dt><dd>{m.registrationNumber || <span className="lab-muted">—</span>}</dd>
            <dt>Phone</dt><dd>{m.contact?.phoneNumber?.phoneNumber || user?.phoneNumber || "—"}</dd>
            <dt>Email</dt><dd>{m.contact?.email || "—"}</dd>
            <dt>Staff ID</dt><dd>{m.staffId || "—"}</dd>
            <dt>Username</dt><dd>{user?.username || m.login?.username || "—"}</dd>
            <dt>Role at this lab</dt><dd><Badge kind="role">{labRole}</Badge></dd>
          </dl>
        )}
      </Section>
      <Section title="Signature">
        <div className="lab-muted" style={{ marginBottom: 10 }}>
          {isSignatory ? "Printed in the signature block of every report you verify." : "Only needed for signatories."}
        </div>
        {staffId && <SignatureUpload staffId={staffId} hasSignature={!!m.signatureImageId} />}
      </Section>
    </div>
  );
};

const LabSwitcher = ({ labs, labId, switchLab, toast }) => {
  const [switching, setSwitching] = useState(null);
  const doSwitch = async (id) => {
    setSwitching(id);
    try { await switchLab(id); toast.success("Switched lab — every screen now shows the new lab"); }
    catch (e) { toast.error(e); } finally { setSwitching(null); }
  };
  return (
    <Section title="Your labs">
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(labs || []).map((l) => (
          <div key={l.labId} className={`lab-list__item ${l.labId === labId ? "selected" : ""}`} style={{ cursor: "default" }}>
            <div>
              <div style={{ fontWeight: 600 }}>{l.labId === labId ? "Current lab" : "Lab"} <span className="lab-muted">· {l.labId}</span></div>
              <div className="lab-muted">Role: {cap(l.role)}{l.isActive === false ? " · inactive" : ""}</div>
            </div>
            {l.labId !== labId && l.isActive !== false && <Button size="sm" loading={switching === l.labId} onClick={() => doSwitch(l.labId)}>Switch</Button>}
          </div>
        ))}
      </div>
    </Section>
  );
};

const Security = ({ changePassword, toast }) => {
  const [pw, setPw] = useState({ old: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setErr(null);
    if (!pw.old || !pw.next) return setErr("Enter your current and new password");
    if (pw.next.length < 6) return setErr("New password must be at least 6 characters");
    if (pw.next !== pw.confirm) return setErr("New passwords do not match");
    setBusy(true);
    try { toast.success(await changePassword(pw.old, pw.next)); setPw({ old: "", next: "", confirm: "" }); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Section title="Change password">
      <div className="lab-grid cols-3">
        <Field label="Current password"><input type="password" className="lab-input" value={pw.old} autoComplete="current-password" onChange={(e) => setPw({ ...pw, old: e.target.value })} /></Field>
        <Field label="New password"><input type="password" className="lab-input" value={pw.next} autoComplete="new-password" onChange={(e) => setPw({ ...pw, next: e.target.value })} /></Field>
        <Field label="Confirm new password"><input type="password" className="lab-input" value={pw.confirm} autoComplete="new-password" onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></Field>
      </div>
      <div className="lab-row between" style={{ marginTop: 12 }}>
        <span className="lab-danger-text">{err}</span>
        <Button variant="primary" loading={busy} onClick={submit}>Update password</Button>
      </div>
    </Section>
  );
};

export default Setting;
